// src/utils/vraisemblanceCalculator.js
// Utility for PS/DT → Vraisemblance calculations
// Based on ENHANCED_OPERATIONAL_SCENARIOS_GUIDE.md

/**
 * PS/DT → Vraisemblance Matrix
 * 
 * DT \ PS | 1 (Très faible) | 2 (Faible) | 3 (Élevée) | 4 (Très élevée)
 * --------|-----------------|------------|------------|------------------
 * 1 (Très facile)  | V=2 Moyenne     | V=3 Élevée | V=4 Très é | V=4 Très élevée
 * 2 (Facile)       | V=1 Faible      | V=2 Moyenne| V=3 Élevée | V=4 Très élevée
 * 3 (Difficile)    | V=1 Faible      | V=2 Moyenne| V=3 Élevée | V=3 Élevée
 * 4 (Très difficile)| V=1 Faible     | V=1 Faible | V=2 Moyenne| V=2 Moyenne
 */

const VRAISEMBLANCE_MATRIX = {
    1: { 1: 2, 2: 3, 3: 4, 4: 4 }, // DT=1 (Très facile)
    2: { 1: 1, 2: 2, 3: 3, 4: 4 }, // DT=2 (Facile)
    3: { 1: 1, 2: 2, 3: 3, 4: 3 }, // DT=3 (Difficile)
    4: { 1: 1, 2: 1, 3: 2, 4: 2 }  // DT=4 (Très difficile)
};

/**
 * Filter the modes that count toward the scenario likelihood calculation.
 * Modes with `retained === false` are excluded; missing/undefined `retained`
 * is treated as retained (default true) for legacy data.
 */
export const getRetainedModes = (modes) => {
    if (!Array.isArray(modes)) return [];
    return modes.filter((m) => m && m.retained !== false);
};

/**
 * Compute the scenario vraisemblance from retained modes only.
 * Returns null when no modes are retained (= "as if the operational scenario
 * has not been done yet"; downstream consumers fall back to V=1).
 *
 * @param {Array} modesOperatoires
 * @returns {number|null}
 */
export const getScenarioVraisemblanceFromRetainedModes = (modesOperatoires) => {
    const retained = getRetainedModes(modesOperatoires);
    if (retained.length === 0) return null;
    const vs = retained
        .map((m) => m.vraisemblance)
        .filter((v) => v >= 1 && v <= 4);
    if (vs.length === 0) return null;
    return Math.max(...vs);
};

/**
 * Calculate vraisemblance from PS and DT using the matrix
 * @param {number} ps - Success Probability (1-4)
 * @param {number} dt - Technical Difficulty (1-4)
 * @returns {number} Vraisemblance (1-4)
 */
export const calculateVraisemblance = (ps, dt) => {
    // Validate inputs
    if (!ps || !dt || ps < 1 || ps > 4 || dt < 1 || dt > 4) {
        console.warn(`Invalid PS/DT values: PS=${ps}, DT=${dt}. Defaulting to V=2`);
        return 2; // Default to Moyenne
    }

    return VRAISEMBLANCE_MATRIX[dt][ps];
};

/**
 * Get French label for vraisemblance value
 * @param {number} value - Vraisemblance (1-4)
 * @returns {string} French label
 */
export const getVraisemblanceLabel = (value) => {
    const labels = {
        1: 'Peu vraisemblable',
        2: 'Vraisemblable',
        3: 'Très vraisemblable',
        4: 'Quasi certain'
    };

    return labels[value] || 'Peu vraisemblable';
};

/**
 * Get English label for vraisemblance value
 * @param {number} value - Vraisemblance (1-4)
 * @returns {string} English label
 */
export const getVraisemblanceLabelEn = (value) => {
    const labels = {
        1: 'Unlikely',
        2: 'Likely',
        3: 'Very likely',
        4: 'Almost certain'
    };

    return labels[value] || 'Unlikely';
};

/**
 * Get color for vraisemblance value (for UI)
 * @param {number} value - Vraisemblance (1-4)
 * @returns {string} Color code
 */
export const getVraisemblanceColor = (value) => {
    const colors = {
        1: '#10b981', // Green - Faible
        2: '#f59e0b', // Amber - Moyenne
        3: '#f97316', // Orange - Élevée
        4: '#ef4444'  // Red - Critique
    };

    return colors[value] || colors[2];
};

/**
 * Get background color for vraisemblance value (lighter shade)
 * @param {number} value - Vraisemblance (1-4)
 * @returns {string} Background color code
 */
export const getVraisemblanceBgColor = (value) => {
    const colors = {
        1: '#d1fae5', // Light green
        2: '#fef3c7', // Light amber
        3: '#fed7aa', // Light orange
        4: '#fee2e2'  // Light red
    };

    return colors[value] || colors[2];
};

/**
 * Get French label for Success Probability (PS)
 * @param {number} ps - Success Probability (1-4)
 * @returns {string} French label
 */
export const getPSLabel = (ps) => {
    const labels = {
        1: 'Faible',
        2: 'Moyenne',
        3: 'Élevée',
        4: 'Critique'
    };

    return labels[ps] || 'Non défini';
};

/**
 * Get French label for Technical Difficulty (DT)
 * @param {number} dt - Technical Difficulty (1-4)
 * @returns {string} French label
 */
export const getDTLabel = (dt) => {
    const labels = {
        1: 'Très facile',
        2: 'Facile',
        3: 'Difficile',
        4: 'Très difficile'
    };

    return labels[dt] || 'Non défini';
};

/**
 * Aggregate vraisemblance values from multiple actions
 * @param {Array} actions - Array of actions with vraisemblance values
 * @param {string} method - Aggregation method ('average', 'weighted', 'minimum')
 * @returns {number} Aggregated vraisemblance (1-4)
 */
export const aggregateVraisemblance = (actions, method = 'average') => {
    if (!actions || actions.length === 0) {
        return 2; // Default to Moyenne
    }

    const vraisemblances = actions
        .map(a => a.vraisemblance)
        .filter(v => v >= 1 && v <= 4);

    if (vraisemblances.length === 0) {
        return 2;
    }

    switch (method) {
        case 'average':
            const sum = vraisemblances.reduce((acc, v) => acc + v, 0);
            return Math.round(sum / vraisemblances.length);

        case 'weighted':
            // Weight by phase importance (EXPLOITER > TROUVER > RENTRER > CONNAITRE)
            const weights = actions.map(a => {
                switch (a.phase) {
                    case 'EXPLOITER': return 4;
                    case 'TROUVER': return 3;
                    case 'RENTRER': return 2;
                    case 'CONNAITRE': return 1;
                    default: return 1;
                }
            });

            const weightedSum = vraisemblances.reduce((acc, v, i) =>
                acc + (v * weights[i]), 0
            );
            const totalWeight = weights.reduce((acc, w) => acc + w, 0);

            return Math.round(weightedSum / totalWeight);

        case 'minimum':
            // Most conservative - use minimum vraisemblance
            return Math.min(...vraisemblances);

        case 'critical_path':
            // Use average of mandatory actions only
            const mandatoryActions = actions.filter(a =>
                a.branchType === 'mandatory' || !a.branchType
            );

            if (mandatoryActions.length === 0) {
                return Math.round(sum / vraisemblances.length);
            }

            const mandatoryV = mandatoryActions
                .map(a => a.vraisemblance)
                .filter(v => v >= 1 && v <= 4);

            const mandatorySum = mandatoryV.reduce((acc, v) => acc + v, 0);
            return Math.round(mandatorySum / mandatoryV.length);

        default:
            return Math.round(sum / vraisemblances.length);
    }
};

/**
 * Validate PS and DT values
 * @param {number} ps - Success Probability
 * @param {number} dt - Technical Difficulty
 * @returns {object} Validation result { valid: boolean, errors: string[] }
 */
export const validatePSDT = (ps, dt) => {
    const errors = [];

    if (ps === undefined || ps === null) {
        errors.push('PS (Probabilité de Succès) est requis');
    } else if (ps < 1 || ps > 4) {
        errors.push('PS doit être entre 1 et 4');
    }

    if (dt === undefined || dt === null) {
        errors.push('DT (Difficulté Technique) est requis');
    } else if (dt < 1 || dt > 4) {
        errors.push('DT doit être entre 1 et 4');
    }

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Get all possible PS/DT combinations with their vraisemblance
 * Useful for displaying the full matrix
 * @returns {Array} Array of { ps, dt, v, psLabel, dtLabel, vLabel }
 */
export const getFullMatrix = () => {
    const matrix = [];

    for (let dt = 1; dt <= 4; dt++) {
        for (let ps = 1; ps <= 4; ps++) {
            const v = calculateVraisemblance(ps, dt);
            matrix.push({
                ps,
                dt,
                v,
                psLabel: getPSLabel(ps),
                dtLabel: getDTLabel(dt),
                vLabel: getVraisemblanceLabel(v),
                color: getVraisemblanceColor(v),
                bgColor: getVraisemblanceBgColor(v)
            });
        }
    }

    return matrix;
};

/**
 * Calculate scenario-level vraisemblance from elementary actions
 * @param {Array} elementaryActions - Array of elementary actions
 * @param {string} calculationMethod - 'express', 'standard', or 'advanced'
 * @returns {object} { vraisemblance, details }
 */
export const calculateScenarioVraisemblance = (elementaryActions, calculationMethod = 'advanced') => {
    if (!elementaryActions || elementaryActions.length === 0) {
        return {
            vraisemblance: 2,
            details: {
                method: calculationMethod,
                message: 'Aucune action élémentaire disponible'
            }
        };
    }

    // Calculate vraisemblance for each action if not already set
    const actionsWithV = elementaryActions.map(action => {
        if (action.vraisemblance) {
            return action;
        }

        if (action.successProbability && action.technicalDifficulty) {
            return {
                ...action,
                vraisemblance: calculateVraisemblance(
                    action.successProbability,
                    action.technicalDifficulty
                )
            };
        }

        return { ...action, vraisemblance: 2 };
    });

    const vraisemblance = aggregateVraisemblance(actionsWithV, 'average');

    return {
        vraisemblance,
        details: {
            method: calculationMethod,
            actionsCount: actionsWithV.length,
            individualVraisemblances: actionsWithV.map(a => ({
                id: a.elementaryActionId,
                phase: a.phase,
                ps: a.successProbability,
                dt: a.technicalDifficulty,
                v: a.vraisemblance
            })),
            aggregationMethod: 'average'
        }
    };
};

/**
 * STANDARD METHOD: Calculate Mode Opératoire vraisemblance using PS only
 * Formula: V(Mode) = MIN(PS of all actions)
 * 
 * Logic:
 * - The mode's vraisemblance equals the minimum PS (weakest link)
 * - No DT consideration - simplified calculation
 * 
 * @param {Array} elementaryActions - Array of elementary actions in this mode
 * @returns {object} { vraisemblance, minPS, details }
 */
export const calculateModeOperatoireVraisemblanceStandard = (elementaryActions) => {
    if (!elementaryActions || elementaryActions.length === 0) {
        return {
            vraisemblance: 2,
            minPS: null,
            details: {
                method: 'standard',
                message: 'Aucune action élémentaire disponible'
            }
        };
    }

    // Extract PS values from actions
    const psValues = elementaryActions
        .map(a => a.successProbability || a.ps)
        .filter(v => v >= 1 && v <= 4);

    if (psValues.length === 0) {
        return {
            vraisemblance: 2,
            minPS: null,
            details: {
                method: 'standard',
                message: 'Valeurs PS manquantes ou invalides'
            }
        };
    }

    // V(Mode) = MIN(PS) - direct, no matrix
    const minPS = Math.min(...psValues);
    const vraisemblance = minPS; // Direct assignment, no matrix lookup

    return {
        vraisemblance,
        minPS,
        details: {
            method: 'standard',
            formula: 'V(Mode) = MIN(PS)',
            minPS: {
                value: minPS,
                label: getPSLabel(minPS),
                explanation: 'Vraisemblance = Probabilité de succès minimale (maillon faible)'
            },
            actionsCount: elementaryActions.length,
            psValues
        }
    };
};

/**
 * ADVANCED METHOD: Calculate Mode Opératoire vraisemblance using PS/DT Matrix
 * Formula: V(Mode) = MATRIX[MAX(DT)][MIN(PS)]
 * 
 * Logic:
 * - MIN(PS): The mode is only as likely as its weakest link (lowest success probability)
 * - MAX(DT): The mode is as difficult as its hardest step (highest technical difficulty)
 * - Uses the full ANSSI vraisemblance matrix
 * 
 * @param {Array} elementaryActions - Array of elementary actions in this mode
 * @returns {object} { vraisemblance, minPS, maxDT, details }
 */
export const calculateModeOperatoireVraisemblanceAdvanced = (elementaryActions) => {
    if (!elementaryActions || elementaryActions.length === 0) {
        return {
            vraisemblance: 2,
            minPS: null,
            maxDT: null,
            details: {
                method: 'advanced',
                message: 'Aucune action élémentaire disponible'
            }
        };
    }

    // Extract PS and DT values from actions
    const psValues = elementaryActions
        .map(a => a.successProbability || a.ps)
        .filter(v => v >= 1 && v <= 4);

    const dtValues = elementaryActions
        .map(a => a.technicalDifficulty || a.dt)
        .filter(v => v >= 1 && v <= 4);

    if (psValues.length === 0) {
        return {
            vraisemblance: 2,
            minPS: null,
            maxDT: null,
            details: {
                method: 'advanced',
                message: 'Valeurs PS manquantes ou invalides'
            }
        };
    }

    // If no DT values, default to DT=2 (Facile)
    const minPS = Math.min(...psValues);
    const maxDT = dtValues.length > 0 ? Math.max(...dtValues) : 2;

    // Use the vraisemblance matrix: V = MATRIX[maxDT][minPS]
    const vraisemblance = calculateVraisemblance(minPS, maxDT);

    return {
        vraisemblance,
        minPS,
        maxDT,
        details: {
            method: 'advanced',
            formula: 'V(Mode) = MATRIX[MAX(DT)][MIN(PS)]',
            minPS: {
                value: minPS,
                label: getPSLabel(minPS),
                explanation: 'Probabilité de succès minimale (maillon faible)'
            },
            maxDT: {
                value: maxDT,
                label: getDTLabel(maxDT),
                explanation: 'Difficulté technique maximale (étape la plus difficile)'
            },
            actionsCount: elementaryActions.length,
            psValues,
            dtValues
        }
    };
};

/**
 * Legacy function - defaults to Advanced method for backward compatibility
 * @deprecated Use calculateModeOperatoireVraisemblanceAdvanced or calculateModeOperatoireVraisemblanceStandard
 */
export const calculateModeOperatoireVraisemblance = (elementaryActions) => {
    return calculateModeOperatoireVraisemblanceAdvanced(elementaryActions);
};

/**
 * Calculate Scenario vraisemblance from Modes Opératoires
 * Formula: V(Scenario) = MAX(V(Mode1), V(Mode2), ..., V(ModeN))
 * 
 * Logic:
 * - The scenario is as likely as its EASIEST/MOST PROBABLE attack path
 * - An attacker will choose the mode with the highest vraisemblance (easiest to execute)
 * 
 * @param {Array} modesOperatoires - Array of modes opératoires, each with elementaryActions
 * @param {string} method - 'standard' (PS only) or 'advanced' (PS/DT matrix)
 * @returns {object} { vraisemblance, maxMode, allModes, details }
 */
export const calculateScenarioVraisemblanceFromModes = (modesOperatoires, method = 'advanced') => {
    if (!modesOperatoires || modesOperatoires.length === 0) {
        return {
            vraisemblance: 2,
            maxMode: null,
            allModes: [],
            details: {
                method,
                message: 'Aucun mode opératoire disponible'
            }
        };
    }

    // Only retained modes contribute to the scenario vraisemblance
    const retainedModes = getRetainedModes(modesOperatoires);
    if (retainedModes.length === 0) {
        return {
            vraisemblance: null,
            maxMode: null,
            allModes: [],
            details: {
                method,
                message: 'Aucun mode opératoire retenu'
            }
        };
    }

    // Calculate vraisemblance for each mode using the specified method
    const modesWithVraisemblance = retainedModes.map(mode => {
        const actions = mode.elementaryActions || mode.actions || [];

        let modeCalc;
        if (method === 'standard') {
            modeCalc = calculateModeOperatoireVraisemblanceStandard(actions);
        } else {
            modeCalc = calculateModeOperatoireVraisemblanceAdvanced(actions);
        }

        return {
            modeId: mode.id || mode.name,
            modeName: mode.name,
            vraisemblance: modeCalc.vraisemblance,
            minPS: modeCalc.minPS,
            maxDT: modeCalc.maxDT || null,
            actionsCount: actions.length,
            method
        };
    });

    // Find the maximum vraisemblance (easiest mode)
    const maxVraisemblance = Math.max(...modesWithVraisemblance.map(m => m.vraisemblance));
    const maxMode = modesWithVraisemblance.find(m => m.vraisemblance === maxVraisemblance);

    return {
        vraisemblance: maxVraisemblance,
        maxMode,
        allModes: modesWithVraisemblance,
        details: {
            method,
            formula: 'V(Scenario) = MAX(V(Mode1), V(Mode2), ..., V(ModeN))',
            explanation: method === 'standard'
                ? 'Standard: V(Mode) = MIN(PS), puis V(Scenario) = MAX des modes'
                : 'Avancé: V(Mode) = MATRIX[MAX(DT)][MIN(PS)], puis V(Scenario) = MAX des modes',
            modesCount: modesOperatoires.length,
            vraisemblanceDistribution: {
                1: modesWithVraisemblance.filter(m => m.vraisemblance === 1).length,
                2: modesWithVraisemblance.filter(m => m.vraisemblance === 2).length,
                3: modesWithVraisemblance.filter(m => m.vraisemblance === 3).length,
                4: modesWithVraisemblance.filter(m => m.vraisemblance === 4).length
            }
        }
    };
};

export default {
    calculateVraisemblance,
    getVraisemblanceLabel,
    getVraisemblanceLabelEn,
    getVraisemblanceColor,
    getVraisemblanceBgColor,
    getPSLabel,
    getDTLabel,
    aggregateVraisemblance,
    validatePSDT,
    getFullMatrix,
    calculateScenarioVraisemblance,
    calculateModeOperatoireVraisemblance,
    calculateModeOperatoireVraisemblanceStandard,
    calculateModeOperatoireVraisemblanceAdvanced,
    calculateScenarioVraisemblanceFromModes
};
